<template>
  <section class="cfo-panel margin-gap-panel" :class="scope.clsPrefix">
    <div class="panel-head">
      <div class="panel-title">毛利率经营关差动作</div>
      <span class="tag danger">净影响 {{ netImpact }}</span>
    </div>
    <div v-if="loading" class="panel-body loading-state">数据加载中...</div>
    <div v-else-if="error" class="panel-body error-state">数据加载失败，请稍后重试</div>
    <template v-else>
      <div class="panel-body wf-section">
        <div class="wf-chart">
          <div
            v-for="(row, index) in waterfallRows"
            :key="index"
            :class="['wf-row', row.rowClass]"
          >
            <div class="wf-label" :style="row.labelStyle">{{ row.label }}</div>
            <div class="wf-bar-wrap">
              <div class="wf-bar" :style="row.barStyle">
                <span class="wf-bar-text">{{ row.barText }}</span>
              </div>
            </div>
            <div class="wf-val" :style="row.valStyle">{{ row.value }}</div>
          </div>
        </div>
      </div>
      <div class="panel-divider"></div>
      <div class="panel-body table-section">
        <div class="table-scroll">
          <table class="gap-table">
            <colgroup>
              <col style="width:11%">
              <col style="width:14%">
              <col style="width:24%">
              <col style="width:13%">
              <col style="width:13%">
              <col style="width:14%">
              <col style="width:11%">
            </colgroup>
            <thead>
              <tr>
                <th>归因端</th>
                <th>影响因素</th>
                <th>影响机制</th>
                <th class="num">毛利额影响</th>
                <th class="num">毛利率影响</th>
                <th>责任</th>
                <th>状态</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in events" :key="index">
                <td>
                  <span :class="['source-tag', row.sourceClass]">{{ row.source }}</span>
                </td>
                <td class="factor">{{ row.factor }}</td>
                <td class="mechanism" :title="row.mechanism">{{ row.mechanism }}</td>
                <td class="num">
                  <span :class="row.amountClass">{{ row.amount }}</span>
                </td>
                <td class="num">
                  <span :class="row.impactClass">{{ row.marginImpact }}</span>
                </td>
                <td class="owner">{{ row.owner }}</td>
                <td>
                  <span :class="['status-text', row.statusClass]">{{ row.status }}</span>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td colspan="3" class="summary-label">负向因素合计，正向动作对冲后</td>
                <td class="num">
                  <span class="negative">{{ summary.amount }}</span>
                </td>
                <td class="num">
                  <span class="negative">{{ summary.impact }}</span>
                </td>
                <td colspan="2" class="summary-note">{{ summary.note }}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </template>
  </section>
</template>

<script>
import { fetchGapClosing, unwrapDashboardResponse } from '@/api/dashboard'

/**
 * API物料代码 → 中文显示名映射
 */
const LABEL_MAP = {
  MAT_STEEL: '钢材成本上涨',
  MAT_COPPER: '铜材料涨价',
  MAT_PCB: 'PCB材料涨价',
  MAT_OTHER: '其他材料上涨'
}

/**
 * 状态 → 状态中文/样式映射
 */
const STATUS_CONFIG = {
  normal: { label: '正常', class: 'done' },
  正常: { label: '正常', class: 'done' },
  urgent: { label: '紧急', class: 'urgent' },
  紧急: { label: '紧急', class: 'urgent' },
  risk: { label: '风险', class: 'risk' },
  风险: { label: '风险', class: 'risk' },
  progressing: { label: '进行中', class: 'progress' },
  进行中: { label: '进行中', class: 'progress' },
  recoverable: { label: '可追回', class: 'done' },
  可追回: { label: '可追回', class: 'done' },
  done: { label: '已完成', class: 'done' },
  已完成: { label: '已完成', class: 'done' }
}

function resolveStatusConfig(status, isNeg) {
  if (status && STATUS_CONFIG[status]) {
    return STATUS_CONFIG[status]
  }
  const text = String(status || '').trim()
  if (text.includes('紧急')) return STATUS_CONFIG.紧急
  if (text.includes('风险')) return STATUS_CONFIG.风险
  if (text.includes('进行')) return STATUS_CONFIG.进行中
  if (text.includes('追回') || text.includes('完成') || text === '正常') {
    return STATUS_CONFIG[text] || STATUS_CONFIG.正常
  }
  if (!text && isNeg) {
    return STATUS_CONFIG.紧急
  }
  return { label: text || '待确认', class: 'progress' }
}

export default {
  name: 'CfoMarginEvents',
  data() {
    return {
      scope: {
        clsPrefix: 'rvc-cosmoplat-workspace-cfo-margin-events'
      },
      waterfallRows: [],
      events: [],
      loading: false,
      error: false,
      netImpact: '',
      summary: {
        amount: '-668万',
        impact: '-1.65pct',
        note: '优先：替代料验证+锁价谈判'
      }
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    /**
     * 加载毛利率关差数据
     */
    async loadData() {
      this.loading = true
      this.error = false
      try {
        const res = await fetchGapClosing()
        const data = unwrapDashboardResponse(res)
        if (!data || data.target_gm == null || data.actual_gm == null) {
          throw new Error('关差数据格式异常')
        }
        this.buildWaterfall(data)
        this.buildTable(data)
        this.buildSummary(data)
        const net = (data.actual_gm - data.target_gm) * 100
        this.netImpact = (net >= 0 ? '+' : '') + net.toFixed(1) + 'pct'
      } catch (e) {
        console.error('获取毛利率关差数据失败:', e)
        this.error = true
      } finally {
        this.loading = false
      }
    },
    /**
     * 构建瀑布图数据
     * @param {Object} data API返回的关差数据
     */
    buildWaterfall(data) {
      const { target_gm, actual_gm, bars, detail } = data
      const rows = []
      const BLUE_GRADIENT = 'linear-gradient(270deg, #4A5FF3 0%, #4E86FF 100%)'
      const GREEN_GRADIENT = 'linear-gradient(270deg, #379700 0%, #46BF00 100%)'
      const LABEL_COLOR = '#333333'
      const ACTUAL_LABEL_COLOR = '#4A5FF3'

      rows.push({
        label: '目标毛利率',
        rowClass: 'is-base',
        labelStyle: { color: LABEL_COLOR, fontWeight: 400 },
        barStyle: { width: '100%', backgroundImage: BLUE_GRADIENT },
        barText: (target_gm * 100).toFixed(1) + '%',
        value: (target_gm * 100).toFixed(1) + '%',
        valStyle: { color: LABEL_COLOR, fontWeight: 600 }
      })

      let maxNegPct = 0
      const negBars = (bars || []).filter(b => b.direction === 'down' && b.value_pct > 0)
      negBars.forEach(b => { maxNegPct = Math.max(maxNegPct, b.value_pct) })

      negBars.forEach(b => {
        const label = LABEL_MAP[b.label] || b.label
        const widthPct = maxNegPct > 0 ? Math.max((b.value_pct / maxNegPct) * 72, 22) : 22
        rows.push({
          label,
          labelStyle: { color: LABEL_COLOR, fontWeight: 400 },
          barStyle: {
            width: widthPct + '%',
            backgroundImage: BLUE_GRADIENT
          },
          barText: (b.value_pct * 100).toFixed(2) + 'pct',
          value: '-' + Math.abs(b.value_amt) + '万',
          valStyle: { color: LABEL_COLOR, fontWeight: 600 }
        })
      })

      const posItems = (detail || []).filter(d => d.impact_amt > 0)
      let maxPosPct = 0
      posItems.forEach(d => { maxPosPct = Math.max(maxPosPct, d.impact_pct) })
      const refPct = Math.max(maxNegPct, maxPosPct) || 1

      posItems.forEach(d => {
        const widthPct = refPct > 0 ? Math.max((d.impact_pct / refPct) * 72, 22) : 22
        rows.push({
          label: d.factor,
          labelStyle: { color: LABEL_COLOR, fontWeight: 400 },
          barStyle: {
            width: widthPct + '%',
            backgroundImage: GREEN_GRADIENT
          },
          barText: '+' + (d.impact_pct * 100).toFixed(2) + 'pct',
          value: '+' + d.impact_amt + '万',
          valStyle: { color: LABEL_COLOR, fontWeight: 600 }
        })
      })

      const actualWidth = target_gm > 0
        ? Math.max(Math.min((actual_gm / target_gm) * 100, 100), 28)
        : 60
      rows.push({
        label: '实际毛利率',
        rowClass: 'wf-total',
        labelStyle: { color: ACTUAL_LABEL_COLOR, fontWeight: 700 },
        barStyle: { width: actualWidth + '%', backgroundImage: BLUE_GRADIENT },
        barText: (actual_gm * 100).toFixed(1) + '%',
        value: (actual_gm * 100).toFixed(1) + '%',
        valStyle: { color: LABEL_COLOR, fontWeight: 700 }
      })

      this.waterfallRows = rows
    },
    /**
     * 构建明细表数据
     * @param {Object} data API返回的关差数据
     */
    buildTable(data) {
      const { detail } = data
      if (!Array.isArray(detail)) {
        this.events = []
        return
      }
      this.events = detail.map((d) => {
        const isNeg = d.impact_amt < 0
        const sourceClass = isNeg ? 'warning' : 'success'
        const statusConf = resolveStatusConfig(d.status, isNeg)
        return {
          source: d.attribution_end || '-',
          sourceClass,
          factor: LABEL_MAP[d.factor] || d.factor,
          mechanism: d.mechanism || d.factor_desc || LABEL_MAP[d.factor] || d.factor,
          amount: (d.impact_amt >= 0 ? '+' : '') + d.impact_amt + '万',
          amountClass: isNeg ? 'negative' : 'positive',
          marginImpact: (d.impact_amt >= 0 ? '+' : '') + (d.impact_pct * 100).toFixed(2) + 'pct',
          impactClass: isNeg ? 'negative' : 'positive',
          owner: d.responsible || '-',
          status: statusConf.label,
          statusClass: statusConf.class
        }
      })
    },
    /**
     * 构建底部汇总行
     * @param {Object} data API返回的关差数据
     */
    buildSummary(data) {
      const detail = Array.isArray(data.detail) ? data.detail : []
      const totalAmt = detail.reduce((sum, d) => sum + (Number(d.impact_amt) || 0), 0)
      const totalPct = detail.reduce((sum, d) => sum + (Number(d.impact_pct) || 0), 0)
      this.summary = {
        amount: (totalAmt >= 0 ? '+' : '') + Math.round(totalAmt) + '万',
        impact: (totalPct >= 0 ? '+' : '') + (totalPct * 100).toFixed(2) + 'pct',
        note: '优先：替代料验证+锁价谈判'
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.rvc-cosmoplat-workspace-cfo-margin-events {
  margin-bottom: 0;
  min-height: 0;
  background: #fff;
  border-radius: 4px;
  padding: 16px;
  box-sizing: border-box;
  box-shadow: none;
  border: none;

  .panel-head {
    padding: 0;
    margin-bottom: 10px;
    border-bottom: none;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-height: 20px;
  }

  .panel-title {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: #1e2226;
    line-height: 20px;
  }

  .tag.danger {
    flex-shrink: 0;
    font-size: 11px;
    padding: 3px 10px;
    border-radius: 999px;
    background: #ffe8e8;
    color: #e54b3d;
    font-weight: 600;
  }

  .panel-body {
    padding: 0;

    &.wf-section {
      padding: 2px 0 14px;
    }

    &.table-section {
      padding-top: 4px;
    }
  }

  .panel-divider {
    border-top: 1px dashed #d9dfec;
    margin: 0 0 2px;
  }

  .wf-chart {
    position: relative;
  }

  .wf-row {
    display: grid;
    grid-template-columns: 120px minmax(0, 1fr) 72px;
    gap: 12px;
    align-items: center;
    margin-bottom: 10px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .wf-label {
    font-size: 12px;
    color: #333333;
    text-align: left;
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .wf-row.wf-total .wf-label {
    color: #4a5ff3;
    font-weight: 700;
  }

  .wf-bar-wrap {
    height: 26px;
    background: #f0f2f5;
    border-radius: 999px;
    overflow: hidden;
    position: relative;
  }

  .wf-bar {
    height: 100%;
    border-radius: 999px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 0 12px;
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    min-width: 56px;
    box-sizing: border-box;
  }

  .wf-bar-text {
    white-space: nowrap;
  }

  .wf-val {
    font-size: 12px;
    font-weight: 600;
    color: #333333;
    text-align: right;
    font-variant-numeric: tabular-nums;
  }

  .table-scroll {
    overflow-x: auto;
    scrollbar-width: thin;
  }

  .gap-table {
    width: 100%;
    min-width: 780px;
    border-collapse: collapse;

    th,
    td {
      padding: 11px 10px;
      border: none;
      border-bottom: 1px dashed #e2e8f0;
      text-align: left;
      vertical-align: middle;
      font-size: 12px;
      line-height: 1.45;
      color: #334155;
      background: transparent;
    }

    th {
      padding-top: 8px;
      padding-bottom: 8px;
      font-size: 11px;
      color: #94a3b8;
      font-weight: 600;
      border-bottom: 1px dashed #d9dfec;
    }

    tbody tr:nth-child(even) td {
      background: #f5f7fc;
    }

    tbody tr:nth-child(odd) td {
      background: #fff;
    }

    tbody tr:last-child td {
      border-bottom: 1px dashed #d9dfec;
    }

    tfoot td {
      background: #fff;
      font-weight: 700;
      font-size: 12px;
      color: #334155;
      border-bottom: none;
      padding-top: 12px;
      padding-bottom: 8px;
    }

    .num {
      text-align: right;
      font-variant-numeric: tabular-nums;
    }

    .factor {
      font-weight: 600;
      color: #1f2937;
      word-break: break-all;
    }

    .mechanism {
      color: #64748b;
      white-space: normal;
      word-break: break-word;
      line-height: 1.5;
    }

    .owner {
      color: #64748b;
      word-break: break-all;
    }

    .summary-label {
      color: #475569;
      font-weight: 600;
    }

    .summary-note {
      color: #64748b;
      font-weight: 600;
    }
  }

  .source-tag {
    display: inline-block;
    font-size: 12px;
    padding: 0;
    border-radius: 0;
    font-weight: 600;
    white-space: nowrap;
    background: transparent;

    &.warning {
      color: #ea580c;
    }

    &.success {
      color: #52c41a;
    }
  }

  .negative {
    color: #e54b3d;
    font-weight: 700;
  }

  .positive {
    color: #52c41a;
    font-weight: 700;
  }

  .status-text {
    font-size: 12px;
    font-weight: 700;
    white-space: nowrap;

    &.urgent {
      color: #e54b3d;
    }

    &.risk {
      color: #f59e0b;
    }

    &.progress {
      color: #64748b;
    }

    &.done {
      color: #52c41a;
    }
  }

  .loading-state,
  .error-state {
    padding: 28px;
    text-align: center;
    font-size: 12px;
    color: #94a3b8;
  }

  .error-state {
    color: #dc2626;
  }
}

@media screen and (max-width: 1100px) {
  .rvc-cosmoplat-workspace-cfo-margin-events {
    .wf-row {
      grid-template-columns: 96px minmax(0, 1fr) 56px;
      gap: 8px;
    }

    .wf-label {
      font-size: 11px;
    }

    .wf-bar-wrap {
      height: 24px;
    }
  }
}
</style>
